<script setup lang="ts">
import type { Ref } from "vue";
import { computed, ref, toRefs } from "vue";
import Spinner from "@/components/helpers/Spinner.vue";
import { isExchangeChannel } from "@/composables/useBillings.js";
import { BILLING_PARTY_INSTITUTION, ORDER_STATUS } from "@/constants.js";
import { CHECKOUT_SUBMIT_BUTTON_LABELS } from "../constants";
import isGroupVisaEligible from "../utils/isGroupVisaEligible";
import InstitutionPaysDeposit from "./InstitutePaysDeposit.vue";
import StudentPaysDeposit from "./StudentPaysDeposit.vue";
import type { CheckoutOrderSubmitPayload, CheckoutProps } from "./types";

const props = defineProps<CheckoutProps>();

const { application } = toRefs(props);

/** Always read from `application` via computed so billing (incl. exchange) stays reactive — do not destructure refs once at setup. */
const appliedAgreement = computed(() => application.value?.appliedAgreement?.value);
const primaryOrder = computed(() => application.value?.primaryOrder?.value);
const profile = computed(() => application.value?.profile?.value);
const studentFormioPaymentSubmission = computed(() => application.value?.studentFormioPaymentSubmission?.value);
const homeInstitution = computed(() => application.value?.homeInstitution?.value);

const submitOrder = async (payload: CheckoutOrderSubmitPayload) => {
  const orderId = currentOrder.value?.id;
  if (!orderId) {
    // Reaching checkout without a primary order is a bug — the order should have
    // been created earlier in the flow. Fail loudly rather than silently creating one.
    throw new Error("Cannot submit order at checkout: no primary order found");
  }
  return application.value.updateOrder(orderId, payload, "Checkout.updateOrderStatus");
};
const registerLoadingState = (name: string, loadingRef: Ref<boolean>) =>
  application.value.registerLoadingState(name, loadingRef);

const session = computed(() => application.value?.session?.value);
const buttonLabel = computed(() =>
  session.value?.requires_application
    ? CHECKOUT_SUBMIT_BUTTON_LABELS.application
    : CHECKOUT_SUBMIT_BUTTON_LABELS.registration,
);
const eligibleForGroupVisa = computed(() => isGroupVisaEligible(profile.value, session.value));

const isLoading = ref(false);
const isComponentLoading = ref(false);
const payment_id = ref(null);
const currentOrder = computed(() => primaryOrder.value);

const APPLICATION_LOADER = "checkout-step";
registerLoadingState(APPLICATION_LOADER, isComponentLoading);

// Computed
const orderIsInDraft = computed(() => currentOrder.value?.status === ORDER_STATUS.draft);
const readOnlyPaymentForm = computed(() => !orderIsInDraft.value);

const hostInstitutionId = computed(() => {
  return session.value?.session_hosting_institutions?.[0]?.entity_id;
});

const opeId = computed(() => {
  return homeInstitution.value?.ope_id ? String(homeInstitution.value?.ope_id).padStart(8, "0") : null;
});

const isExchangeWithoutOrderContract = computed(() => {
  const fromPageSession = session.value;
  const fromOrder = currentOrder.value?.session;
  const isEx = isExchangeChannel(fromPageSession) || isExchangeChannel(fromOrder);
  return isEx && !currentOrder.value?.billing_contract_id;
});

const institutionPays = computed(() => {
  if (studentFormioPaymentSubmission.value?.formName === "institutionPayment") {
    return true;
  }
  if (isExchangeWithoutOrderContract.value) {
    return false;
  }
  return appliedAgreement.value?.applicationFee === BILLING_PARTY_INSTITUTION;
});

const orderUpdatePayload = computed(() => {
  return {
    ...currentOrder.value,
    status: ORDER_STATUS.submitted,
    application_fee_payment_id: !institutionPays.value ? payment_id.value : null,
    billing_contract_id: appliedAgreement.value?.contract_number ?? null,
    ope_id: opeId.value,
    eligible_for_group_visa: eligibleForGroupVisa.value,
    host_institution_id: hostInstitutionId.value,
  };
});

const updateOrderStatus = async (paymentId = null) => {
  isComponentLoading.value = true;
  payment_id.value = paymentId;

  // update order in learning service
  const orderPayload = orderUpdatePayload.value;
  await submitOrder(orderPayload as CheckoutOrderSubmitPayload);

  await props.goToNextStep();
  isComponentLoading.value = false;
};

const nextStep = async () => {
  isComponentLoading.value = true;
  await props.goToNextStep();
  isComponentLoading.value = false;
};
</script>

<template>
  <div
    class="text-indigo-base text-left bg-white w-full px-4 pb-4 border-collapse md:p-0"
  >
    <template v-if="isLoading">
      <spinner>
        <template #spinnercontent>
          <p class="text-gray-600">
            Loading checkout step, please wait.
          </p>
        </template>
      </spinner>
    </template>
    <template v-else>
      <institution-pays-deposit
        v-if="institutionPays"
        :application="application"
        :existing-payment-data="studentFormioPaymentSubmission"
        :is-read-only="readOnlyPaymentForm"
        :button-label="buttonLabel"
        @update-order="updateOrderStatus"
        @next-step="nextStep"
      />
      <student-pays-deposit v-else
        :application="application"
        :existing-payment-data="studentFormioPaymentSubmission"
        :is-read-only="readOnlyPaymentForm"
        :button-label="buttonLabel"
        @update-order="updateOrderStatus"
        @next-step="nextStep" />
    </template>
  </div>
</template>